import { FormEvent, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { billingsApi, projectsApi } from '@/api/endpoints'
import type { Billing, BillingPreview, Project } from '@/api/types'
import { Button, Card, Field, Input, Modal, Select } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatHours, formatMoney } from '@/utils/format'

const DATE = 'YYYY-MM-DD'

/** Vyúčtování se skoro vždycky dělá za uzavřený měsíc, tak ať je po ruce. */
const PERIODS = [
  {
    label: 'Minulý měsíc',
    range: () => {
      const prev = dayjs().subtract(1, 'month')
      return [prev.startOf('month').format(DATE), prev.endOf('month').format(DATE)] as const
    },
  },
  {
    label: 'Tento měsíc',
    range: () => [dayjs().startOf('month').format(DATE), dayjs().endOf('month').format(DATE)] as const,
  },
]

const formatPeriod = (from: string, to: string) => `${dayjs(from).format('D. M.')} – ${dayjs(to).format('D. M. YYYY')}`

export default function BillingsPage() {
  const [billings, setBillings] = useState<Billing[]>([])
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Billing | null>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [invoicing, setInvoicing] = useState(false)
  const [issuing, setIssuing] = useState(false)
  const [uninvoicing, setUninvoicing] = useState<Billing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ number: string | null; publicUrl: string | null } | null>(null)

  const load = async () => setBillings(await billingsApi.list())

  useEffect(() => {
    load().catch((err) => setError(errorMessage(err, 'Vyúčtování se nepodařilo načíst.')))
  }, [])

  const open = billings.filter((b) => b.status === 'OPEN')
  const picked = open.filter((b) => selected.includes(b.id))

  // Faktura umí jednoho klienta a jednu měnu — Fakturoid nic jiného neunese.
  const invoiceBlocker = useMemo(() => {
    if (picked.length === 0) return 'Vyber vyúčtování k fakturaci'
    if (new Set(picked.map((b) => b.clientId)).size > 1) return 'Vybraná vyúčtování patří různým klientům'
    if (new Set(picked.map((b) => b.currency)).size > 1) return 'Vybraná vyúčtování jsou v různých měnách'
    return null
  }, [picked])

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const remove = async (billing: Billing) => {
    setError(null)
    try {
      await billingsApi.remove(billing.id)
      setSelected((prev) => prev.filter((id) => id !== billing.id))
      await load()
    } catch (err) {
      setError(errorMessage(err, 'Zrušení se nepovedlo.'))
    }
  }

  const cancelInvoice = async (billing: Billing) => {
    if (!billing.invoice) return
    setError(null)
    try {
      await billingsApi.removeInvoice(billing.invoice.id)
      setUninvoicing(null)
      await load()
    } catch (err) {
      setError(errorMessage(err, 'Fakturaci se nepodařilo zrušit.'))
    }
  }

  const issueInvoice = async (taxableFulfillmentDue: string) => {
    setInvoicing(true)
    setError(null)
    try {
      const invoice = await billingsApi.createInvoice(
        picked.map((b) => b.id),
        taxableFulfillmentDue,
      )
      setIssued({ number: invoice.number, publicUrl: invoice.publicUrl })
      setSelected([])
      setIssuing(false)
      await load()
    } catch (err) {
      setError(errorMessage(err, 'Fakturu se nepodařilo vystavit.'))
    } finally {
      setInvoicing(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Vyúčtování</h1>
        <Button onClick={() => setCreating(true)}>Nové vyúčtování</Button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      {issued && (
        <Card className="border-emerald-200 bg-emerald-50">
          <p className="text-sm text-emerald-800">
            Faktura {issued.number ?? ''} je ve Fakturoidu — odeslání klientovi zůstává na tobě.{' '}
            {issued.publicUrl && (
              <a className="font-medium underline" href={issued.publicUrl} target="_blank" rel="noreferrer">
                Otevřít
              </a>
            )}
          </p>
        </Card>
      )}

      <Card>
        {billings.length === 0 ? (
          <p className="text-sm text-slate-400">Zatím žádné vyúčtování. Založ ho tlačítkem nahoře.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-400">
                  <th className="py-1 pr-2"></th>
                  <th className="py-1">Období</th>
                  <th className="py-1">Klient · projekt</th>
                  <th className="py-1 text-right">Spočteno</th>
                  <th className="py-1 text-right">Fakturováno</th>
                  <th className="py-1 text-right">Sazba</th>
                  <th className="py-1 text-right">Částka</th>
                  <th className="py-1">Stav</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {billings.map((b) => {
                  const diff = b.billedHours - b.computedHours
                  return (
                    <tr key={b.id} className="border-t border-slate-100">
                      <td className="py-2 pr-2">
                        {b.status === 'OPEN' && (
                          <input type="checkbox" checked={selected.includes(b.id)} onChange={() => toggle(b.id)} />
                        )}
                      </td>
                      <td className="py-2 text-slate-600">{formatPeriod(b.periodFrom, b.periodTo)}</td>
                      <td className="py-2">
                        <span className="text-slate-500">{b.clientName} ·</span> {b.projectName}
                        {b.note && <div className="text-xs text-slate-400">{b.note}</div>}
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums text-slate-500">
                        {b.computedHours.toFixed(2)} h
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums">
                        {b.billedHours.toFixed(2)} h
                        {Math.abs(diff) >= 0.005 && (
                          <span className="ml-1 text-xs text-amber-600">
                            ({diff > 0 ? '+' : ''}
                            {diff.toFixed(2)})
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right text-slate-500">{formatMoney(b.rate, b.currency)}</td>
                      <td className="py-2 text-right font-medium">{formatMoney(b.amount, b.currency)}</td>
                      <td className="py-2">
                        {b.status === 'OPEN' ? (
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">otevřené</span>
                        ) : b.invoice?.publicUrl ? (
                          <a
                            className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:underline"
                            href={b.invoice.publicUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {b.invoice.number ?? 'faktura'}
                          </a>
                        ) : (
                          <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                            vyfakturováno
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {b.status === 'OPEN' ? (
                          <>
                            <button className="text-xs text-brand-600 hover:underline" onClick={() => setEditing(b)}>
                              upravit
                            </button>
                            <button className="ml-3 text-xs text-red-500 hover:underline" onClick={() => remove(b)}>
                              zrušit
                            </button>
                          </>
                        ) : (
                          b.invoice && (
                            <button
                              className="text-xs text-red-500 hover:underline"
                              onClick={() => setUninvoicing(b)}
                              title="Vrátí vyúčtování mezi otevřená"
                            >
                              zrušit fakturaci
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {open.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <span className="text-sm text-slate-500">{invoiceBlocker ?? `Vybráno ${picked.length}`}</span>
            <Button onClick={() => setIssuing(true)} disabled={!!invoiceBlocker}>
              Vystavit fakturu
            </Button>
          </div>
        )}
      </Card>

      {creating && (
        <CreateBillingModal
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await load()
          }}
        />
      )}

      {editing && (
        <EditBillingModal
          billing={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}

      {issuing && (
        <IssueInvoiceModal
          billings={picked}
          busy={invoicing}
          onClose={() => setIssuing(false)}
          onIssue={issueInvoice}
        />
      )}

      {uninvoicing && (
        <Modal title="Zrušit fakturaci" onClose={() => setUninvoicing(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Vyúčtování <span className="font-medium">{uninvoicing.projectName}</span> (
              {formatPeriod(uninvoicing.periodFrom, uninvoicing.periodTo)}) se vrátí mezi otevřená a půjde znovu
              upravit i vyfakturovat. Týká se to všech vyúčtování na faktuře{' '}
              {uninvoicing.invoice?.number ?? ''}.
            </p>
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              Doklad ve Fakturoidu tím <span className="font-medium">nezmizí</span> — pokud tam ještě je, smaž ho
              zvlášť. Do cizího účetnictví aplikace sama nesahá.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setUninvoicing(null)}>
                Zpět
              </Button>
              <Button type="button" variant="danger" onClick={() => cancelInvoice(uninvoicing)}>
                Zrušit fakturaci
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

/**
 * Vystavení faktury. Jediné, co se tu rozhoduje, je DUZP — předvyplněné na
 * poslední den fakturovaného období, protože to je datum, ke kterému byla
 * práce odvedená. Backend si stejný default drží taky, tohle ho jen zpřístupní.
 */
function IssueInvoiceModal({
  billings,
  busy,
  onClose,
  onIssue,
}: {
  billings: Billing[]
  busy: boolean
  onClose: () => void
  onIssue: (taxableFulfillmentDue: string) => void
}) {
  const lastDay = useMemo(
    () => billings.map((b) => b.periodTo).sort().at(-1) ?? dayjs().format(DATE),
    [billings],
  )
  const [duzp, setDuzp] = useState(lastDay)

  const total = billings.reduce((sum, b) => sum + b.amount, 0)
  const currency = billings[0]?.currency ?? ''

  return (
    <Modal title="Vystavit fakturu" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onIssue(duzp)
        }}
        className="space-y-4"
      >
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Klient</span>
            <span className="font-medium">{billings[0]?.clientName}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-slate-500">Vyúčtování</span>
            <span>{billings.length}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-slate-500">Celkem bez DPH</span>
            <span className="font-medium">{formatMoney(total, currency)}</span>
          </div>
        </div>

        <div className="w-48">
          <Field label="DUZP">
            <Input type="date" value={duzp} onChange={(e) => setDuzp(e.target.value)} required />
          </Field>
        </div>
        {duzp !== lastDay && (
          <p className="text-xs text-amber-700">
            Poslední den fakturovaného období je {dayjs(lastDay).format('D. M. YYYY')}.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Zrušit
          </Button>
          <Button type="submit" disabled={busy || !duzp}>
            {busy ? 'Vystavuji…' : 'Vystavit'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function CreateBillingModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [projects, setProjects] = useState<Project[]>([])
  const [projectId, setProjectId] = useState('')
  const [from, setFrom] = useState(() => PERIODS[0].range()[0])
  const [to, setTo] = useState(() => PERIODS[0].range()[1])
  const [preview, setPreview] = useState<BillingPreview | null>(null)
  const [hours, setHours] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    projectsApi.list().then(setProjects)
  }, [])

  // Náhled táhneme při každé změně projektu i období, ať uživatel vidí,
  // kolik hodin se do vyúčtování opravdu chytne.
  useEffect(() => {
    if (!projectId) return setPreview(null)
    let current = true
    setError(null)
    billingsApi
      .preview(projectId, from, to)
      .then((data) => {
        if (!current) return
        setPreview(data)
        setHours(data.suggestedHours.toFixed(2))
      })
      .catch((err) => current && setError(errorMessage(err, 'Náhled se nepovedl.')))
    return () => {
      current = false
    }
  }, [projectId, from, to])

  const applyPeriod = (preset: (typeof PERIODS)[number]) => {
    const [f, t] = preset.range()
    setFrom(f)
    setTo(t)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!projectId) return setError('Vyber projekt.')
    const billedHours = Number(hours.replace(',', '.'))
    if (!Number.isFinite(billedHours) || billedHours < 0) return setError('Fakturované hodiny nedávají smysl.')

    setSaving(true)
    setError(null)
    try {
      await billingsApi.create({ projectId, periodFrom: from, periodTo: to, billedHours, note: note || undefined })
      onCreated()
    } catch (err) {
      setError(errorMessage(err, 'Vyúčtování se nepodařilo založit.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Nové vyúčtování" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Projekt">
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Vyber projekt…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.clientName} · {p.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex flex-wrap gap-2">
          {PERIODS.map((p) => {
            const [f, t] = p.range()
            const active = f === from && t === to
            return (
              <Button
                key={p.label}
                type="button"
                variant={active ? 'primary' : 'secondary'}
                className="px-3 py-1.5"
                onClick={() => applyPeriod(p)}
              >
                {p.label}
              </Button>
            )
          })}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="min-w-40 flex-1">
            <Field label="Od">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
          </div>
          <div className="min-w-40 flex-1">
            <Field label="Do">
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
        </div>

        {preview && (
          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Spočteno ze záznamů</span>
              <span className="font-mono tabular-nums">
                {preview.computedHours.toFixed(2)} h ({formatHours(preview.computedSecs)}, {preview.entryCount} zázn.)
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span className="text-slate-500">Sazba</span>
              <span>{formatMoney(preview.rate, preview.currency)} / h</span>
            </div>
            {preview.vatRate > 0 && (
              <div className="mt-1 flex justify-between">
                <span className="text-slate-500">DPH</span>
                <span>{preview.vatRate} %</span>
              </div>
            )}
            {preview.lockedCount > 0 && (
              <p className="mt-2 text-xs text-amber-700">
                {preview.lockedCount} záznamů v období už patří jinému vyúčtování — sem se nedostanou.
              </p>
            )}
            {preview.entryCount === 0 && (
              <p className="mt-2 text-xs text-red-600">V období nejsou žádné nevyúčtované hodiny.</p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Field label="Fakturovat hodin">
              <Input
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                inputMode="decimal"
                className="font-mono tabular-nums"
              />
            </Field>
          </div>
          {preview && (
            <span className="pb-2 text-sm text-slate-500">
              = {formatMoney(Number(hours.replace(',', '.') || 0) * preview.rate, preview.currency)}
            </span>
          )}
        </div>

        <Field label="Poznámka">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Připíše se na řádek faktury" />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Zrušit
          </Button>
          <Button type="submit" disabled={saving || !preview || preview.entryCount === 0}>
            {saving ? 'Zakládám…' : 'Uzavřít vyúčtování'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function EditBillingModal({
  billing,
  onClose,
  onSaved,
}: {
  billing: Billing
  onClose: () => void
  onSaved: () => void
}) {
  const [hours, setHours] = useState(billing.billedHours.toFixed(2))
  const [note, setNote] = useState(billing.note ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const billedHours = Number(hours.replace(',', '.'))
    if (!Number.isFinite(billedHours) || billedHours < 0) return setError('Fakturované hodiny nedávají smysl.')

    setSaving(true)
    setError(null)
    try {
      await billingsApi.update(billing.id, { billedHours, note })
      onSaved()
    } catch (err) {
      setError(errorMessage(err, 'Uložení se nepovedlo.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={`${billing.clientName} · ${billing.projectName}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-500">
          {formatPeriod(billing.periodFrom, billing.periodTo)} · spočteno{' '}
          <span className="font-mono">{billing.computedHours.toFixed(2)} h</span> z {billing.entryCount} záznamů
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Field label="Fakturovat hodin">
              <Input
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                inputMode="decimal"
                className="font-mono tabular-nums"
              />
            </Field>
          </div>
          <span className="pb-2 text-sm text-slate-500">
            = {formatMoney(Number(hours.replace(',', '.') || 0) * billing.rate, billing.currency)}
          </span>
        </div>

        <Field label="Poznámka">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Zrušit
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Ukládám…' : 'Uložit'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
